import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { FormDocumentPicker } from "@/components/Form/FormDocumentPicker";
import { FormImagePicker } from "@/components/Form/FormImagePicker";
import { FormVideoPicker } from "@/components/Form/FormVideoPicker";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { MAX_AMOUNT_IMAGES_PER_POST } from "@/helpers/community-feed";
import {
  createDocumentSizeRule,
  createDocumentTypeRule,
  createImageSizeRule,
  createImageTypeRule,
  createVideoSizeRule,
  createVideoTypeRule,
} from "@/helpers/rules";
import { useTranslation } from "@/translations";

import type { CommunityMessageEditFormValues } from "./CommunityMessageEditForm";

export function CommunityMessageAttachmentField(): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();

  const form = useFormContext<CommunityMessageEditFormValues>();
  const currImages = useWatch({ control: form.control, name: "images" });
  const currVideos = useWatch({
    control: form.control,
    name: "videos",
  });
  const currDocuments = useWatch({
    control: form.control,
    name: "documents",
  });
  const error =
    form.formState.errors["images"]?.message ||
    form.formState.errors["videos"]?.message ||
    form.formState.errors["documents"]?.message;

  const isAnyAttachmentSelected = currDocuments.length > 0 || currImages.length > 0 || currVideos.length > 0;

  return (
    <FormField label={t("page.create-post.form.file")} hideLabel>
      <Stack gap="xs">
        <Inline gap="sm" align="center">
          {(!isAnyAttachmentSelected || currImages.length > 0) && (
            <FormImagePicker<CommunityMessageEditFormValues, "images">
              name="images"
              maxFiles={MAX_AMOUNT_IMAGES_PER_POST}
              disabled={currImages.length >= MAX_AMOUNT_IMAGES_PER_POST}
              rules={{
                validate: {
                  size: createImageSizeRule({ t, onError: () => postHog.capture("form_error_image_size_exceeded") }),
                  type: createImageTypeRule({ t }),
                },
              }}
            />
          )}
          {(!isAnyAttachmentSelected || currVideos.length > 0) && (
            <FormVideoPicker<CommunityMessageEditFormValues, "videos">
              name="videos"
              maxFiles={1}
              disabled={currVideos.length > 0}
              rules={{
                validate: {
                  size: createVideoSizeRule({ t, onError: () => postHog.capture("form_error_video_size_exceeded") }),
                  type: createVideoTypeRule({ t }),
                },
              }}
            />
          )}
          {(!isAnyAttachmentSelected || currDocuments.length > 0) && (
            <FormDocumentPicker<CommunityMessageEditFormValues, "documents">
              name="documents"
              maxFiles={1}
              disabled={currDocuments.length > 0}
              rules={{
                validate: {
                  size: createDocumentSizeRule({
                    t,
                    onError: () => postHog.capture("form_error_document_size_exceed"),
                  }),
                  type: createDocumentTypeRule({ t }),
                },
              }}
            />
          )}
        </Inline>
        {Boolean(error) && (
          <span
            data-testid="form-input-error"
            role="alert"
            className="text-overline whitespace-nowrap text-text-danger-secondary"
          >
            {error}
          </span>
        )}
      </Stack>
    </FormField>
  );
}
